import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './lib/auth';
import { NavigationGuard } from './lib/UnsavedChanges';
import { AppShell } from './lib/AppShell';
import { WizardLayout } from './lib/WizardLayout';
import { AuthLayout } from './lib/AuthLayout';
import { LandingPage } from './routes/LandingPage';
import { LoginPage } from './routes/LoginPage';
import { RegisterPage } from './routes/RegisterPage';
import { ForgotPasswordPage } from './routes/ForgotPasswordPage';
import { ResetPasswordPage } from './routes/ResetPasswordPage';
import { SuspendedPage } from './routes/SuspendedPage';
import { DashboardPage } from './routes/DashboardPage';
import { RequireAuth } from './routes/RequireAuth';
import { StructuresListPage } from './routes/StructuresListPage';
import { StructureFormPage } from './routes/StructureFormPage';
import { ProjectCreatePage, ProjectsListPage } from './routes/ProjectsListPage';
import { ProjectDetailPage } from './routes/ProjectDetailPage';
import { ProjectSummaryPage } from './routes/ProjectSummaryPage';
import { AdminPage } from './routes/AdminPage';
import { AdminFoundriesPage } from './routes/AdminFoundriesPage';
import { AdminUserDetailPage } from './routes/AdminUserDetailPage';
import { SettingsPage } from './routes/SettingsPage';
import { AccountPage } from './routes/AccountPage';
import { MenuPage } from './routes/MenuPage';
import { StructureSelectionPage } from './routes/StructureSelectionPage';
import { StructureConfigurePage } from './routes/StructureConfigurePage';
import { StructureDimensionsPage } from './routes/StructureDimensionsPage';
import { StructureNotesPage } from './routes/StructureNotesPage';
import { StructureReportPage } from './routes/StructureReportPage';
// Heavy (html2pdf.js / html2canvas / jsPDF) and only reached in a new tab —
// code-split so it stays out of the main bundle.
const StructurePdfPage = lazy(() =>
  import('./routes/StructurePdfPage').then((m) => ({ default: m.StructurePdfPage })),
);
const ProjectPdfPage = lazy(() =>
  import('./routes/ProjectPdfPage').then((m) => ({ default: m.ProjectPdfPage })),
);
import { AddStructureToProjectPage } from './routes/AddStructureToProjectPage';
import { AddStructuresToProjectPage } from './routes/AddStructuresToProjectPage';
import { CastingLookupPage } from './routes/CastingLookupPage';
import { HelpReferencePage } from './routes/HelpReferencePage';

/**
 * Root layout: mounts the global unsaved-changes navigation guard above the
 * routed content so it can observe every route transition.
 */
function RootLayout() {
  return (
    <>
      <NavigationGuard />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/welcome', element: <LandingPage /> },
      { path: '/register', element: <RegisterPage /> },
      // Standalone print-optimised Structure PDF page (opened in a new tab).
      // No wizard/app chrome — mirrors the legacy `structure/pdf.blade.php`.
      // Public: mirrors the legacy `/delivered/report/structure/{token}` screen,
      // which sat outside the auth middleware so shared email recipients could
      // view the report. Data comes from the anonymous `.../delivered` endpoints.
      {
        path: '/structure/pdf/:id',
        element: (
          <Suspense fallback={<div className="container p-4 text-muted">Loading…</div>}>
            <StructurePdfPage />
          </Suspense>
        ),
      },
      // Public project report/PDF page — mirrors the legacy
      // `/delivered/report/project/{token}` screen (also outside auth middleware).
      {
        path: '/project/pdf/:id',
        element: (
          <Suspense fallback={<div className="container p-4 text-muted">Loading…</div>}>
            <ProjectPdfPage />
          </Suspense>
        ),
      },
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/password/reset', element: <ResetPasswordPage /> },
          { path: 'suspended', element: <SuspendedPage /> },
        ],
      },
      // End-user wizard surface (Phase K). No sidebar; minimal chrome
      // shared via WizardLayout.
      {
        element: (
          <RequireAuth>
            <WizardLayout />
          </RequireAuth>
        ),
        children: [
          { path: '/', element: <MenuPage /> },
          { path: '/newstructure', element: <StructureConfigurePage /> },
          { path: '/newstructure/select', element: <StructureSelectionPage /> },
          { path: '/newstructure/notes', element: <StructureNotesPage /> },
          { path: '/newstructure/dimensions', element: <StructureDimensionsPage /> },
          { path: '/newstructure/casting', element: <CastingLookupPage /> },
          { path: '/structurereport/view/:id', element: <StructureReportPage /> },
          { path: '/structurereport/:id', element: <StructureReportPage /> },
          { path: '/structure/addtoproject/:id', element: <AddStructureToProjectPage /> },
          { path: '/help', element: <HelpReferencePage /> },
        ],
      },
      // Admin / management surface (Phase K.0 reorg).
      {
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { path: '/admin', element: <DashboardPage /> },
          { path: '/admin/structures', element: <StructuresListPage /> },
          { path: '/admin/structures/new', element: <StructureFormPage /> },
          { path: '/admin/structures/:id', element: <StructureFormPage /> },
          { path: '/admin/projects', element: <ProjectsListPage /> },
          { path: '/newprojectfolder', element: <ProjectCreatePage /> },
          { path: '/project/addstructure/:id', element: <AddStructuresToProjectPage /> },
          { path: '/admin/projects/new', element: <ProjectCreatePage /> },
          { path: '/admin/projects/:id/summary', element: <ProjectSummaryPage /> },
          { path: '/admin/projects/:id', element: <ProjectDetailPage /> },
          { path: '/admin/console', element: <AdminPage /> },
          { path: '/admin/foundries', element: <AdminFoundriesPage /> },
          { path: '/admin/console/users/:id', element: <AdminUserDetailPage /> },
          { path: '/admin/settings', element: <SettingsPage /> },
          { path: '/admin/account', element: <AccountPage /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
